import axios from "axios";
import { getClientId } from "./clientId";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "fg_access_token";

export function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function setStoredToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore — private mode */
  }
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach the persistent client id + Bearer token to every request.
// We dual-send the token (cookie + header) because iOS Safari's ITP
// drops SameSite=None;Secure cookies in many partitioned contexts,
// which causes "logged in but 401 on next call" symptoms on mobile.
api.interceptors.request.use((config) => {
  try {
    config.headers["X-Client-Id"] = getClientId();
  } catch {
    /* ignore */
  }
  const token = getStoredToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Phase D-8 — attach guest token when present + no real user is logged in.
  // The backend honours X-Guest-Token only for guest-scope endpoints.
  if (!token && !config.headers["X-Guest-Token"]) {
    try {
      const gt = localStorage.getItem("fg_guest_token");
      if (gt) config.headers["X-Guest-Token"] = gt;
    } catch { /* ignore */ }
  }
  return config;
});

// On any 401, clear the stored token so the AuthProvider's checkSession
// resolves to logged-out cleanly instead of looping.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      setStoredToken(null);
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
